import { jwtDecode } from 'jwt-decode';
import { nodeClient } from '../../common/environment';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface DecodedToken {
  exp: number;
}

let refreshTokenPromise: Promise<AuthTokens> | null = null;

export const storeTokens = (tokens: AuthTokens): void => {
  localStorage.setItem('userTokens', JSON.stringify(tokens));
};

export const clearTokens = (): void => {
  localStorage.removeItem('userTokens');
};

/**
 * Performs the token refresh API call. Throws an error on failure.
 */
const performTokenRefresh = async (
  refreshToken: string
): Promise<AuthTokens> => {
  const response = await nodeClient.post<{ accessToken: string }>(
    `auth/refresh-token`,
    {
      refreshToken,
    }
  );

  const newTokens: AuthTokens = {
    accessToken: response.data.accessToken,
    refreshToken: refreshToken,
  };

  storeTokens(newTokens);
  return newTokens;
};

/**
 * Retrieves valid authentication tokens or throws an error if unable to do so.
 * If the access token is expired, it will automatically attempt to refresh it.
 *
 * @returns A promise that resolves to AuthTokens if successful.
 * @throws An error if the session is expired and cannot be refreshed or if no tokens are available.
 */
export const getTokens = async (): Promise<AuthTokens> => {
  const storedTokens = localStorage.getItem('userTokens');
  if (!storedTokens) {
    throw new Error('No session available');
  }

  const tokens = JSON.parse(storedTokens) as AuthTokens;

  const decodedToken: DecodedToken = jwtDecode(tokens.accessToken);
  const isTokenExpired = decodedToken.exp * 1000 < Date.now() + 60000;

  if (!isTokenExpired) {
    return tokens;
  }

  if (!tokens.refreshToken) {
    throw new Error('Session expired: No refresh token available.');
  }

  if (!refreshTokenPromise) {
    refreshTokenPromise = performTokenRefresh(tokens.refreshToken).finally(
      () => {
        refreshTokenPromise = null;
      }
    );
  }

  return refreshTokenPromise;
};

export const tokenManager = {
  storeTokens,
  clearTokens,
  getTokens,
};
